import { FiUsers, FiFolder, FiDollarSign, FiActivity, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';
import exchangeRateService from '../services/exchangeRateService';

const DashboardPage = () => {
  const { admin, isSuperAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]); // Real data state
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_CONFIG.BASE_URL}/api/platform/stats`);
        if (response.data.success) {
          const platformData = response.data.data.platform;
          const recentActivityData = response.data.data.recentActivity || []; // Get recent activity

          setRecentActivities(recentActivityData); // Set recent activities

          setStats([
            {
              name: 'Total Users',
              value: platformData.totalUsers.toLocaleString(),
              icon: FiUsers,
            },
            {
              name: 'Projects Funded',
              value: platformData.projectsFunded.toString(),
              icon: FiFolder,
            },
            {
              name: 'Active Campaigns',
              value: platformData.activeCampaigns.toString(),
              icon: FiActivity,
            },
            {
              name: 'Total Funding',
              // Display both currencies
              value: `π ${parseFloat(platformData.totalRaised.pi).toLocaleString()} + ₦ ${parseFloat(platformData.totalRaised.ngn).toLocaleString()}`,
              icon: FiDollarSign,
            },
            {
              name: 'Platform Activity. (Success Rate)',
              value: `${platformData.successRate}%`,
              icon: FiActivity,
            },
          ]);
        } else {
          throw new Error(response.data.error?.message || 'Failed to fetch stats');
        }
      } catch (err) {
        console.error('Error fetching platform stats:', err);
        setError('Failed to load platform statistics. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    if (!isSuperAdmin()) return;

    try {
      setRateLoading(true);
      const rateData = await exchangeRateService.getCurrentRate();
      const formattedRate = exchangeRateService.formatRateForDisplay(rateData);
      setExchangeRate(formattedRate);
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
      // Don't show error for missing rate, it's expected when first set up
    } finally {
      setRateLoading(false);
    }
  };

  // Helper to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-h3 text-neutral-900">
                Welcome back, {admin?.username}!
              </CardTitle>
              <CardDescription className="text-p2-r text-neutral-600 mt-1">
                Here's what's happening with your platform today.
              </CardDescription>
            </div>
            <div className="text-right">
              <p className="text-p3-r text-neutral-500">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <p className="text-p3-r text-neutral-400 mt-1">
                Role: {admin?.role}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-teal mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading statistics...</p>
          </div>
        ) : error ? (
          <div className="col-span-full text-center py-8">
            <p className="text-red-600 mb-2">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        ) : (
          stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.name} className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-primary-teal/10 rounded-md flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary-teal" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-p3-r text-neutral-500">{stat.name}</p>
                      <div className="flex items-baseline">
                        <p className="text-h4 text-neutral-900">
                          {stat.value}
                        </p>
                        <p className={`ml-2 text-p3-b ${stat.changeType === 'increase'
                          ? 'text-green-600'
                          : 'text-red-600'
                          }`}>
                          {stat.change}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Exchange Rate Card (SuperAdmin only) */}
      {isSuperAdmin() && (
        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <FiTrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-p3-r text-neutral-500">Exchange Rate</p>
                  <p className="text-p3-b text-neutral-900">Pi to NGN</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/exchange-rate')}
                className="text-purple-600 hover:text-purple-700"
              >
                Manage
              </Button>
            </div>

            {rateLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                <span className="ml-2 text-sm text-gray-600">Loading...</span>
              </div>
            ) : exchangeRate ? (
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {exchangeRate.formattedRate}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Last updated: {exchangeRate.updatedAt}
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No rate set</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/exchange-rate')}
                  className="mt-2"
                >
                  Set Rate
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-l1-b text-neutral-900">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-primary-teal rounded-full mt-2"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-p2-r text-neutral-900">{activity.message}</p>
                    <p className="text-p3-r text-neutral-500 mt-1">
                      {formatTimeAgo(activity.time)} • <span className="text-xs bg-gray-100 px-1 rounded">{activity.admin}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-l1-b text-neutral-900">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button
                variant="ghost"
                className="w-full justify-start h-auto p-4 bg-primary-teal/5 hover:bg-primary-teal/10"
                onClick={() => navigate('/projects')}
              >
                <FiFolder className="h-5 w-5 text-primary-teal mr-3" />
                <span className="text-p2-b text-neutral-900">
                  Review Pending Projects
                </span>
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start h-auto p-4 bg-blue-50 hover:bg-blue-100"
                onClick={() => navigate('/withdrawals')}
              >
                <FiDollarSign className="h-5 w-5 text-blue-600 mr-3" />
                <span className="text-p2-b text-neutral-900">
                  Process Withdrawals
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default DashboardPage;
